import { LookupDto, RankGradeHierarchyLookupDto } from '@siza-staff-care/shared/data';

type RankGradeLookupItem = {
	grade: LookupDto;
	rank: RankGradeHierarchyLookupDto;
};

/**
 * A dictionary of RankGradeLookupItem objects, keyed by the Grade's OID.
 * Can be used to look up a Grade by its OID to get the Grade and Rank objects.
 */
export type RankGradeLookup = Record<string, RankGradeLookupItem>;

/**
 * Builds a RankGradeLookup from a RankGradeHierarchyLookupDto collection.
 * @param ranksGrades
 * @returns
 */
export const buildGradeLookup = (ranksGrades: RankGradeHierarchyLookupDto[]) => {
	const gradeLookup: RankGradeLookup = {};

	ranksGrades.forEach((r) => {
		if (!r.children) {
			return;
		}
		r.children.forEach((g) => {
			gradeLookup[g.oid] = {
				grade: g,
				rank: r,
			};
		});
	});

	return gradeLookup;
};

/**
 * Gets the Grade object by its OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getGradeFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = gradeLookup[gradeOid];
	return grade ? grade.grade : undefined;
};

/**
 * Gets the Grade code by its OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getGradeCodeFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = getGradeFromGradeLookup(gradeLookup, gradeOid);
	return grade ? grade.code : '';
};

/**
 * Gets the Grade description by its OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getGradeDescriptionFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = getGradeFromGradeLookup(gradeLookup, gradeOid);
	return grade ? grade.desc : '';
};

/**
 * Gets the Rank description by its grade OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getRankDescriptionFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const rank = getRankFromGradeLookup(gradeLookup, gradeOid);
	return rank ? rank.desc : '';
};

/**
 * Gets the Rank object by its grade OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getRankFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = gradeLookup[gradeOid];
	return grade ? grade.rank : undefined;
};

/**
 * Gets the Rank code by its grade OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getRankCodeFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = gradeLookup[gradeOid];
	return grade ? grade.rank.code : '';
};

/**
 * Gets a formatted rank/grade code by the grade's OID from the RankGradeLookup.
 * @param gradeLookup
 * @param gradeOid
 * @returns
 */
export const getRankGradeFromGradeLookup = (gradeLookup: RankGradeLookup, gradeOid: string) => {
	const grade = gradeLookup[gradeOid];
	if (!grade) {
		return '';
	}

	return `${grade.grade.code}/${grade.rank.code}`;
};

export const mapDescToDescAndCode = (lookup: LookupDto) => {
	return {
		...lookup,
		desc: `${lookup.desc} (${lookup.code})`,
	};
};

export type TreeItem = {
	value: string;
	label: string;
	indent: number;
	isDisabled: boolean;
	item: LookupDto;
	items: TreeItem[];
	parent?: string;
};

export interface HierarchyDto extends LookupDto {
	parent?: string;
	isDisabled?: boolean;
}

/**
 * Builds a tree of TreeItems from a list of HierarchyDto objects, using their parent property to build the tree.
 * Returns an ordered list flattened list of TreeItems, with an indent indicating their level in the tree.
 * @param  {Array} hierarchyItems
 */
export const buildTree = (hierarchyItems: HierarchyDto[]): TreeItem[] => {
	const tree: Record<string, TreeItem> = {};
	const parents: TreeItem[] = [];

	if (!hierarchyItems) {
		return [] as TreeItem[];
	}

	for (let i = 0; i < hierarchyItems.length; i++) {
		const item = hierarchyItems[i];
		let parent: TreeItem | undefined = tree[item.parent ?? ''];
		let treeItem: TreeItem = tree[item.oid];
		if (!treeItem) {
			treeItem = { value: item.oid } as TreeItem;
			tree[item.oid] = treeItem;
		}

		treeItem.label = item.desc;
		treeItem.parent = item.parent;
		treeItem.isDisabled = !!item.isDisabled;
		treeItem.item = item;

		if (!parent && item.parent !== undefined) {
			parent = { value: item.parent } as TreeItem;
			tree[item.parent] = parent;
		}

		if (!parent) {
			continue;
		}

		parent.items = parent.items || [];
		parent.items.push(treeItem);
	}

	// Process items in the tree without parents - these are the root items.
	for (const key in tree) {
		const item = tree[key];
		if (item.parent !== undefined) {
			continue;
		}

		if (item.label) {
			parents.push(item);
		} else {
			for (let i = 0; i < item.items.length; i++) {
				parents.push(item.items[i]);
			}
		}
	}

	const flattenedTree: TreeItem[] = [];
	const flattenTree = (item: TreeItem, tree: TreeItem[], indent: number) => {
		const treeItem: TreeItem = {
			value: item.value,
			label: item.label,
			indent: indent,
			isDisabled: item.isDisabled,
			item: item.item,
			items: [],
		};
		tree.push(treeItem);
		if (item.items) {
			for (let i = 0; i < item.items.length; i++) {
				const childItem = flattenTree(item.items[i], tree, indent + 1);
				treeItem.items.push(childItem);
			}
		}

		return treeItem;
	};

	for (let i = 0; i < parents.length; i++) {
		flattenTree(parents[i], flattenedTree, 0);
	}

	return flattenedTree;
};
